import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQueryClient
} from 'react-query';
import { APIErrorType } from '../../api';
import {
  fetchExamStudentQuestions,
  fetchStudentQuestions,
  StudentQuestionOverviewType,
  StudentQuestionParamsType,
  updateStudentQuestionBehaviorState,
  updateStudentQuestionBehaviorStateDataType,
  updateStudentQuestionMarked,
  updateStudentQuestionMarkedDataType,
  updateStudentQuestionOption,
  updateStudentQuestionOptionDataType
} from '../../api/student-question';
import { PagintionType } from '../../utils';

export function useStudentQuestionsInfiniteQuery<StudentQuestionType>(
  params: StudentQuestionParamsType = {}
): UseInfiniteQueryResult<PagintionType<StudentQuestionType>> {
  return useInfiniteQuery<
    PagintionType<StudentQuestionType>,
    APIErrorType,
    PagintionType<StudentQuestionType>,
    [string, StudentQuestionParamsType]
  >(['student-questions', params], fetchStudentQuestions, {
    getNextPageParam: lastPage => (lastPage.nextPage ? lastPage.nextPage : undefined),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

export function useExamStudentQuestionsInfiniteQuery<StudentQuestionOverviewType>(
  examId: number | undefined
): UseInfiniteQueryResult<PagintionType<StudentQuestionOverviewType>> {
  if (!examId) throw Error('examId is undefiend');
  return useInfiniteQuery<
    PagintionType<StudentQuestionOverviewType>,
    APIErrorType,
    PagintionType<StudentQuestionOverviewType>,
    [string, { examId: number }]
  >(['student-questions', { examId }], fetchExamStudentQuestions, {
    getNextPageParam: lastPage => (lastPage.nextPage ? lastPage.nextPage : undefined),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

export function useUpdateStudentQuestionOptionMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    StudentQuestionOverviewType,
    APIErrorType,
    updateStudentQuestionOptionDataType
  >(updateStudentQuestionOption, {
    onSuccess: async studentQuestion => {
      const queryKey = ['student-questions', { examId: studentQuestion.exam }];
      const data: { pages: PagintionType<StudentQuestionOverviewType>[] } | undefined =
        await queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data?.pages.map(page => ({
          ...page,
          data: page.data.map(sq => (studentQuestion.id === sq.id ? studentQuestion : sq))
        }))
      });
    }
  });
}

export function useUpdateStudentQuestionBehaviorStateMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    StudentQuestionOverviewType,
    APIErrorType,
    updateStudentQuestionBehaviorStateDataType
  >(updateStudentQuestionBehaviorState, {
    onSuccess: async studentQuestion => {
      const queryKey = ['student-questions', { examId: studentQuestion.exam }];
      const data: { pages: PagintionType<StudentQuestionOverviewType>[] } | undefined =
        await queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data?.pages.map(page => ({
          ...page,
          data: page.data.map(sq => (studentQuestion.id === sq.id ? studentQuestion : sq))
        }))
      });
    }
  });
}

type StudentQuestionsCache = { pages: PagintionType<StudentQuestionOverviewType>[] } | undefined;
type MarkedMutationContext = { previousQueries: Array<[QueryKey, StudentQuestionsCache]> };

const STUDENT_QUESTIONS_QUERY_KEY: QueryKey = ['student-questions'];

export function useUpdateStudentQuestionMarkedMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    StudentQuestionOverviewType,
    APIErrorType,
    updateStudentQuestionMarkedDataType,
    MarkedMutationContext
  >(updateStudentQuestionMarked, {
    onMutate: async ({ studentQuestionId, marked }) => {
      await queryClient.cancelQueries(STUDENT_QUESTIONS_QUERY_KEY);

      const previousQueries = queryClient.getQueriesData<StudentQuestionsCache>(
        STUDENT_QUESTIONS_QUERY_KEY
      );

      queryClient.setQueriesData<StudentQuestionsCache>(
        STUDENT_QUESTIONS_QUERY_KEY,
        current => {
          if (!current?.pages) return current;
          return {
            ...current,
            pages: current.pages.map(page => ({
              ...page,
              data: page.data.map(sq =>
                sq.id === studentQuestionId ? { ...sq, marked } : sq
              )
            }))
          };
        }
      );

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      context?.previousQueries.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries(STUDENT_QUESTIONS_QUERY_KEY);
    }
  });
}
